import { useEffect, useState } from "react";
import { Search, Armchair, UtensilsCrossed, Coffee, ShoppingBag, ConciergeBell, MapPin } from "lucide-react";
import api from "../lib/api";

/* "Explore This Airport" — unified lounge/food/shop/service search with terminal
   and near-my-gate (same concourse) filtering. Owner spec §1-2, §11, §27-28. */

const CHIPS = [
  { key: "", label: "All", icon: Search },
  { key: "lounges", label: "Lounges", icon: Armchair },
  { key: "food", label: "Food & Drink", icon: UtensilsCrossed },
  { key: "coffee", label: "Coffee", icon: Coffee },
  { key: "shopping", label: "Shopping", icon: ShoppingBag },
  { key: "services", label: "Services", icon: ConciergeBell },
];

export default function AirportExplore({ iata, onOpenLounges, onOpenShops }) {
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("");
  const [gate, setGate] = useState("");
  const [results, setResults] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => { setQ(""); setCat(""); setGate(""); setResults(null); }, [iata]);

  useEffect(() => {
    if (!q && !cat && !gate) { setResults(null); return; }
    setBusy(true);
    const t = setTimeout(() => {
      api.get(`/airports/${iata}/explore`, { params: { q: q || undefined, cat: cat || undefined, gate: gate || undefined } })
        .then((r) => setResults(r.data)).catch(() => setResults({ items: [], count: 0 }))
        .finally(() => setBusy(false));
    }, 350);
    return () => clearTimeout(t);
  }, [q, cat, gate, iata]);

  return (
    <div className="border border-zinc-300 bg-white p-4 sm:p-5" data-testid="airport-explore">
      <div className="relative">
        <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
        <input value={q} onChange={(e) => setQ(e.target.value)} data-testid="airport-explore-input"
          placeholder="What are you looking for at this airport? Lounge, coffee, vegan food, duty-free…"
          className="w-full border border-zinc-300 bg-white pl-10 pr-3 py-3 text-sm focus:outline-none focus:border-blue-600" />
      </div>
      <div className="flex flex-wrap items-center gap-2 mt-3">
        {CHIPS.map((c) => (
          <button key={c.key} onClick={() => setCat(cat === c.key ? "" : c.key)} data-testid={`explore-chip-${c.key || "all"}`}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-700 border transition-colors ${cat === c.key ? "bg-blue-700 border-blue-700 text-white" : "border-zinc-300 bg-white hover:border-blue-500"}`}>
            <c.icon className="w-3.5 h-3.5" /> {c.label}
          </button>
        ))}
        <div className="relative ml-auto">
          <MapPin className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-zinc-400" />
          <input value={gate} onChange={(e) => setGate(e.target.value)} data-testid="explore-gate-input"
            placeholder="Near gate… (e.g. B12)" maxLength={5}
            className="w-36 border border-zinc-300 pl-8 pr-2 py-1.5 text-xs focus:outline-none focus:border-blue-600" />
        </div>
      </div>
      {(results || busy) && (
        <div className="mt-4" data-testid="explore-results">
          {busy && !results ? <p className="text-sm text-muted-foreground">Searching…</p> : results && (
            results.items.length === 0
              ? <p className="text-sm text-muted-foreground">Nothing matched{gate ? " near that gate" : ""} — try a broader search, or browse the Lounges and Dining tabs.</p>
              : (
                <>
                  <p className="text-xs text-muted-foreground mb-2">{results.count} result{results.count === 1 ? "" : "s"}{gate ? ` · same-concourse matching for gate ${gate.toUpperCase()} (walking times not estimated)` : ""}</p>
                  <ul className="divide-y divide-zinc-100 border border-zinc-200 max-h-80 overflow-y-auto">
                    {results.items.map((it) => (
                      <li key={`${it.kind}-${it.id}`}>
                        <button onClick={() => (it.kind === "lounge" ? onOpenLounges() : onOpenShops())} data-testid={`explore-result-${it.id}`}
                          className="w-full flex items-center justify-between gap-3 px-3 py-2.5 text-left hover:bg-blue-50 transition-colors">
                          <span className="min-w-0">
                            <span className="text-sm font-700 text-zinc-900">{it.name}</span>
                            <span className="block text-xs text-muted-foreground truncate">
                              {[it.kind === "lounge" ? it.subcategory : (it.subcategory || it.category),
                                it.terminal, it.concourse && `Concourse ${it.concourse}`,
                                it.cuisines?.slice(0, 2).join(", "), it.dietary?.slice(0, 2).join(", ")]
                                .filter(Boolean).join(" · ")}
                            </span>
                          </span>
                          <span className={`shrink-0 text-[10px] font-700 uppercase px-1.5 py-0.5 ${it.kind === "lounge" ? "bg-violet-100 text-violet-800" : it.category === "food" ? "bg-emerald-100 text-emerald-800" : it.category === "retail" ? "bg-amber-100 text-amber-800" : "bg-zinc-100 text-zinc-700"}`}>
                            {it.kind === "lounge" ? "Lounge" : it.category}
                          </span>
                        </button>
                      </li>
                    ))}
                  </ul>
                </>
              )
          )}
        </div>
      )}
    </div>
  );
}
